import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import {
  CREDIT_ORDERS_COUNT_KEY,
  CREDIT_ORDERS_KEY,
  CREDIT_PACKAGES_KEY,
  CREDIT_WALLET_PROFILE_KEY,
  CREDIT_WALLET_TRANSACTIONS_KEY,
  getCreditFeatureCostKey,
} from 'common/constants/queryKeys'

import {
  getCreditFeatureCost,
  getCreditOrders,
  getCreditOrdersCount,
  getCreditPackages,
  getCreditWalletProfile,
  getCreditWalletTransactions,
} from '.'
import {
  CreditFeatureCost,
  CreditOrder,
  CreditOrdersCountData,
  CreditOrdersResponse,
  CreditPackage,
  CreditWalletProfile,
  CreditWalletTransactionsResponse,
} from './types'

const PAGE_SIZE = 10

export const useGetCreditWalletProfile = (enabled = true) =>
  useQuery<CreditWalletProfile>({
    queryKey: CREDIT_WALLET_PROFILE_KEY,
    queryFn: async () => {
      const { data } = await getCreditWalletProfile()
      return data.data
    },
    enabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

export const useGetCreditWalletTransactions = (transaction_type?: string) => {
  const query = useInfiniteQuery<CreditWalletTransactionsResponse>({
    queryKey: [...CREDIT_WALLET_TRANSACTIONS_KEY, transaction_type],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await getCreditWalletTransactions({
        page: pageParam as number,
        pageSize: PAGE_SIZE,
        transaction_type,
      })
      return data
    },
    getNextPageParam: (lastPage) => lastPage.nextPage ?? null,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
  })

  const transactions = query.data?.pages.flatMap((p) => p.data ?? []) ?? []

  return { ...query, transactions }
}

export const useGetCreditOrders = () => {
  const query = useInfiniteQuery<CreditOrdersResponse>({
    queryKey: CREDIT_ORDERS_KEY,
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await getCreditOrders({ page: pageParam as number, pageSize: 10 })
      return data
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data || lastPage.data.length < 10) return null
      return allPages.length + 1
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

  const orders: CreditOrder[] = query.data?.pages.flatMap((p) => p.data ?? []) ?? []
  return { ...query, orders }
}

export const useGetCreditOrdersCount = (enabled = true) =>
  useQuery<CreditOrdersCountData>({
    queryKey: CREDIT_ORDERS_COUNT_KEY,
    queryFn: async () => {
      const { data } = await getCreditOrdersCount()
      return data.data
    },
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })

export const useGetCreditPackages = () =>
  useQuery<CreditPackage[]>({
    queryKey: CREDIT_PACKAGES_KEY,
    queryFn: async () => {
      const { data } = await getCreditPackages()
      return data.data
    },
    staleTime: 5 * 60 * 1000,
  })

export const useGetCreditFeatureCost = (featureCode: string, enabled = true) =>
  useQuery<CreditFeatureCost>({
    queryKey: getCreditFeatureCostKey(featureCode),
    queryFn: async () => {
      const { data } = await getCreditFeatureCost(featureCode)
      return data.data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
